import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import { X, Plus, Trash2, BookHeart, Award, Activity, AlertCircle, Lock, Users as UsersIcon, FileText, Calendar, Video, Check, Target, QrCode, Download } from "lucide-react";

const ENTRY_TYPES = [
  ["session_entrainement", "Session d'entraînement", Activity],
  ["evaluation", "Évaluation", Award],
  ["promotion_ceinture", "Promotion ceinture", Award],
  ["blessure", "Blessure", AlertCircle],
  ["note_privee_admin", "Note privée (admin)", Lock],
  ["note_partagee_membre", "Note partagée avec membre", UsersIcon],
  ["objectif_personnel", "Objectif personnel", FileText],
  ["antecedent_medical", "Antécédent médical", AlertCircle],
  ["rendez_vous_medical", "Rendez-vous médical", Calendar],
  ["autre", "Autre", FileText],
];

const VISIBILITIES = [
  ["private_admin", "🔒 Privé admin/coachs"],
  ["shared_coaches", "👥 Tous les coachs"],
  ["shared_member", "👁 Visible par le membre"],
];

const SEVERITIES = [["legere", "Légère"], ["moderee", "Modérée"], ["severe", "Sévère"]];

const EMPTY_ENTRY = {
  entry_date: new Date().toISOString().slice(0, 10),
  entry_type: "session_entrainement",
  title: "",
  content: "",
  visibility: "private_admin",
  author_name: "",
  ceinture_nouvelle: "",
  ceinture_precedente: "",
  severity: "",
};

export default function HealthBookDrawer({ contract, onClose, onContractUpdated }) {
  const [entries, setEntries] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_ENTRY);

  const load = useCallback(() => {
    if (!contract?.id) return;
    api.get(`/health-book/${contract.id}`)
      .then((r) => setEntries(r.data))
      .catch(() => {});
  }, [contract?.id]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { contract_id: contract.id, ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      await api.post("/health-book", payload);
      toast.success("Entrée ajoutée au carnet");
      setForm(EMPTY_ENTRY);
      setShowForm(false);
      load();
      if (form.entry_type === "promotion_ceinture" && form.ceinture_nouvelle) {
        onContractUpdated?.();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette entrée du carnet ?")) return;
    try {
      await api.delete(`/health-book/${id}`);
      toast.success("Entrée supprimée");
      load();
    } catch { toast.error("Erreur"); }
  };

  if (!contract) return null;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="healthbook-drawer">
      <div className="absolute inset-0 bg-black/60" onClick={onClose}/>
      <div className="ml-auto w-full max-w-2xl bg-white h-full overflow-y-auto relative animate-in slide-in-from-right" data-testid="healthbook-panel">
        <header className="hard-border-b px-6 py-4 sticky top-0 bg-white z-10 flex items-start justify-between">
          <div>
            <div className="ticker text-muted-foreground mb-1 flex items-center gap-2">
              <BookHeart size={14} className="text-impact-gold"/> CARNET DE SANTÉ ARTS MARTIAUX
            </div>
            <h2 className="font-display font-black text-2xl">{contract.full_name}</h2>
            <div className="text-xs text-muted-foreground mt-1">
              {contract.discipline?.replace("_", " ")} · ceinture: {contract.niveau_ceinture || "—"}
            </div>
          </div>
          <button data-testid="hb-close" onClick={onClose} className="p-1.5 hover:bg-secondary"><X size={18}/></button>
        </header>

        <div className="px-6 py-4">
          <ProgressionPanel contract={contract}/>
          <HealthPassPanel contract={contract}/>
          <button
            data-testid="hb-add-entry-btn"
            onClick={() => setShowForm(!showForm)}
            className="bg-foreground hover:bg-impact-red text-white px-4 py-2 ticker flex items-center gap-2 transition-colors w-full justify-center"
          >
            <Plus size={14}/> {showForm ? "Annuler" : "Nouvelle entrée"}
          </button>

          {showForm && (
            <form onSubmit={submit} className="hard-border p-4 mt-4 space-y-2 bg-secondary/20" data-testid="hb-form">
              <div className="grid grid-cols-2 gap-2">
                <input data-testid="hb-date" type="date" value={form.entry_date} onChange={(e) => setForm({ ...form, entry_date: e.target.value })} className="hard-border px-2 py-1.5 text-sm"/>
                <select data-testid="hb-type" value={form.entry_type} onChange={(e) => setForm({ ...form, entry_type: e.target.value })} className="hard-border px-2 py-1.5 text-sm bg-white">
                  {ENTRY_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <input data-testid="hb-title" required placeholder="Titre court (ex: Sparring intensité 5/10) *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="hard-border px-2 py-1.5 text-sm w-full"/>
              <textarea data-testid="hb-content" placeholder="Détails / observations / progrès / consignes..." value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="hard-border px-2 py-1.5 text-sm w-full" rows={4}/>

              {form.entry_type === "promotion_ceinture" && (
                <div className="grid grid-cols-2 gap-2 p-2 bg-impact-gold/10 hard-border border-impact-gold">
                  <input data-testid="hb-ceinture-prec" placeholder="Ceinture précédente" value={form.ceinture_precedente} onChange={(e) => setForm({ ...form, ceinture_precedente: e.target.value })} className="hard-border px-2 py-1.5 text-sm"/>
                  <input data-testid="hb-ceinture-nouvelle" required placeholder="Nouvelle ceinture *" value={form.ceinture_nouvelle} onChange={(e) => setForm({ ...form, ceinture_nouvelle: e.target.value })} className="hard-border px-2 py-1.5 text-sm"/>
                </div>
              )}

              {form.entry_type === "blessure" && (
                <select data-testid="hb-severity" value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })} className="hard-border px-2 py-1.5 text-sm bg-white w-full">
                  <option value="">Sévérité</option>
                  {SEVERITIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              )}

              <div className="grid grid-cols-2 gap-2">
                <select data-testid="hb-visibility" value={form.visibility} onChange={(e) => setForm({ ...form, visibility: e.target.value })} className="hard-border px-2 py-1.5 text-sm bg-white">
                  {VISIBILITIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <input data-testid="hb-author" placeholder="Coach / auteur" value={form.author_name} onChange={(e) => setForm({ ...form, author_name: e.target.value })} className="hard-border px-2 py-1.5 text-sm"/>
              </div>
              <button data-testid="hb-submit" type="submit" className="bg-impact-red hover:bg-foreground text-white py-2 ticker w-full transition-colors">Ajouter l'entrée</button>
            </form>
          )}

          <div className="mt-6 space-y-3">
            {entries.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground" data-testid="hb-empty">
                <BookHeart size={48} className="mx-auto mb-3 opacity-30"/>
                <p className="text-sm">Carnet de santé vierge. Ajoute la première entrée.</p>
              </div>
            ) : (
              entries.map((e) => {
                const typeData = ENTRY_TYPES.find(([v]) => v === e.entry_type);
                const TypeIcon = typeData?.[2] || FileText;
                const typeLabel = typeData?.[1] || e.entry_type;
                const visIcon = e.visibility === "private_admin" ? "🔒" : (e.visibility === "shared_member" ? "👁" : "👥");
                return (
                  <div key={e.id} className="hard-border p-3" data-testid={`hb-entry-${e.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <TypeIcon size={16} className="text-impact-gold flex-shrink-0"/>
                        <div>
                          <div className="font-medium text-sm">{e.title}</div>
                          <div className="text-xs text-muted-foreground mt-0.5">
                            {e.entry_date} · {typeLabel} · {visIcon} {e.author_name ? `· ${e.author_name}` : ""}
                          </div>
                        </div>
                      </div>
                      <button data-testid={`hb-del-${e.id}`} onClick={() => remove(e.id)} className="p-1 hover:bg-impact-red/10 hover:text-impact-red text-muted-foreground" title="Supprimer"><Trash2 size={12}/></button>
                    </div>
                    {e.entry_type === "promotion_ceinture" && e.ceinture_nouvelle && (
                      <div className="mt-2 text-sm bg-impact-gold/10 px-2 py-1 inline-block font-mono">{e.ceinture_precedente || "—"} → <strong>{e.ceinture_nouvelle}</strong></div>
                    )}
                    {e.entry_type === "blessure" && e.severity && (
                      <div className="mt-2 text-xs uppercase bg-impact-red/10 text-impact-red px-2 py-1 inline-block">Sévérité : {e.severity}</div>
                    )}
                    {e.content && (
                      <div className="mt-2 text-sm whitespace-pre-wrap text-muted-foreground">{e.content}</div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ProgressionPanel({ contract }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    if (!contract?.id) return;
    setLoading(true);
    try {
      const r = await api.get(`/students/${contract.id}/carnet`);
      setData(r.data);
    } catch {
      setData(null);
    }
    setLoading(false);
  }, [contract?.id]);

  useEffect(() => { load(); }, [load]);

  const toggleMastery = async (tech) => {
    try {
      await api.post(`/students/${contract.id}/carnet/technique/${tech.id}/toggle-mastery`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const [validatingId, setValidatingId] = useState(null);
  const [vNote, setVNote] = useState("");
  const [vPercent, setVPercent] = useState(100);
  const openValidate = (t) => {
    setValidatingId(t.id);
    setVNote(t.coach_note || "");
    setVPercent(t.coach_percent != null ? t.coach_percent : (t.percent != null ? t.percent : 100));
  };
  const validateTech = async (tech, decision) => {
    try {
      await api.post(`/students/${contract.id}/carnet/technique/${tech.id}/validate`,
        { decision, percent: Number(vPercent), note: vNote });
      toast.success(decision === "valide" ? "Technique validée ✓" : "Marquée à retravailler");
      setValidatingId(null); setVNote("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const STATUS_META = {
    maitrise: { label: "Maîtrisé", color: "#10b981" },
    en_cours: { label: "En cours", color: "#f59e0b" },
    a_travailler: { label: "À travailler", color: "#f59e0b" },
    a_decouvrir: { label: "À découvrir", color: "#94a3b8" },
  };

  if (loading) return <div className="hard-border p-3 mb-3 text-sm text-muted-foreground" data-testid="progression-loading">Chargement carnet...</div>;
  if (!data) return null;

  // Aucun carnet de progression pour cet élève
  if (data.no_carnet || data.ok === false) {
    return (
      <div className="hard-border p-3 mb-3 bg-impact-gold/5" data-testid="progression-no-carnet">
        <div className="flex items-center gap-2 text-sm">
          <Target size={16} className="text-impact-gold"/>
          <span>Aucun carnet de progression pour cet élève.</span>
        </div>
        <a href="/app/anomalies#carnet-auto" data-testid="progression-generate-link" className="ticker text-impact-red text-xs mt-2 inline-block hover:underline">Générer les carnets par discipline →</a>
      </div>
    );
  }

  const s = data.student || {};
  const summary = data.summary || {};
  const techs = data.techniques || [];
  const avg = summary.global_average;

  return (
    <div className="hard-border p-3 mb-3 bg-impact-gold/5" data-testid="progression-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Target size={16} className="text-impact-gold"/>
          <div className="ticker text-xs text-muted-foreground">PROGRESSION CEINTURE</div>
        </div>
        <button onClick={() => setExpanded(!expanded)} data-testid="progression-toggle" className="ticker text-xs hover:text-impact-red">
          {expanded ? "Réduire" : `Détails (${summary.techniques_count || 0}) →`}
        </button>
      </div>

      {/* Ceinture actuelle → cible */}
      <div className="flex items-center gap-3 mb-2 flex-wrap">
        <span className="font-bold text-sm" data-testid="current-belt">{s.current_belt || "Non évalué"}</span>
        {s.target_belt && (
          <>
            <span className="text-muted-foreground">→</span>
            <span className="text-sm text-muted-foreground" data-testid="target-belt">{s.target_belt}</span>
          </>
        )}
        {avg != null && (
          <span className="ml-auto text-xs font-bold text-impact-gold" data-testid="carnet-average">{avg}% moyen</span>
        )}
      </div>

      {/* Barre moyenne globale */}
      {avg != null && (
        <div className="hard-border h-3 bg-white relative overflow-hidden">
          <div className="bg-impact-gold h-full transition-all" style={{ width: `${avg}%` }} data-testid="progression-bar"/>
        </div>
      )}
      {summary.techniques_count === 0 && (
        <p className="text-xs text-muted-foreground italic mt-1">Carnet sans technique — regénère les carnets par discipline.</p>
      )}
      {summary.techniques_count > 0 && (
        <div className="flex gap-2 mt-2 flex-wrap">
          {Object.entries(summary.by_status || {}).map(([st, n]) => (
            <span key={st} className="text-[10px] px-1.5 py-0.5 rounded"
              style={{ background: `${(STATUS_META[st]?.color || "#94a3b8")}22`, color: STATUS_META[st]?.color || "#94a3b8" }}>
              {STATUS_META[st]?.label || st} : {n}
            </span>
          ))}
        </div>
      )}

      {expanded && (
        <div className="mt-3 pt-3 border-t space-y-1 max-h-80 overflow-y-auto" data-testid="progression-details">
          {techs.length === 0 && (
            <p className="text-xs text-muted-foreground italic">Aucune technique dans ce carnet.</p>
          )}
          {techs.map(t => {
            const meta = STATUS_META[t.status] || STATUS_META.a_decouvrir;
            const mastered = t.status === "maitrise";
            return (
              <div key={t.id} data-testid={`carnet-tech-${t.id}`}
                className="text-xs px-2 py-1 hover:bg-secondary/40">
                <div className="flex items-center gap-2">
                <button onClick={() => toggleMastery(t)} data-testid={`toggle-mastery-${t.id}`}
                  title={mastered ? "Retirer la maîtrise" : "Marquer maîtrisée"}
                  className={`w-4 h-4 rounded-full shrink-0 flex items-center justify-center border transition-colors ${mastered ? "bg-[#10b981] border-[#10b981]" : "border-current hover:border-[#10b981]"}`}
                  style={{ color: mastered ? "#fff" : meta.color }}>
                  {mastered && <Check size={11} />}
                </button>
                <span className={`truncate ${mastered ? "line-through opacity-70" : ""}`}>{t.name}</span>
                {t.percent != null && <span className="text-muted-foreground ml-1">{t.percent}%</span>}
                {t.coach_decision === "valide" && <span className="text-[10px] px-1 rounded bg-emerald-500/20 text-emerald-500 shrink-0" title={t.coach_note || ""}>✓ Kru</span>}
                {t.coach_decision === "a_retravailler" && <span className="text-[10px] px-1 rounded bg-amber-500/20 text-amber-500 shrink-0" title={t.coach_note || ""}>↻ à retravailler</span>}
                {t.validation_requested && !t.coach_decision && <span className="text-[10px] px-1 rounded bg-blue-500/20 text-blue-500 shrink-0">demande validation</span>}
                <button onClick={() => (validatingId === t.id ? setValidatingId(null) : openValidate(t))} data-testid={`validate-open-${t.id}`}
                  className="ml-auto shrink-0 ticker text-[10px] text-impact-red hover:text-impact-gold" title="Valider (Kru)">✎ Valider</button>
                {t.video_url && !/youtu\.?be/i.test(t.video_url) && (
                  <a href={t.video_embed_url || t.video_url} target="_blank" rel="noreferrer" data-testid={`carnet-video-${t.id}`}
                    className="text-impact-red hover:text-impact-gold shrink-0" title="Voir la vidéo">
                    <Video size={13} />
                  </a>
                )}
                </div>
                {t.coach_note && validatingId !== t.id && <div className="pl-6 text-[10px] text-muted-foreground italic mt-0.5">Note Kru : {t.coach_note}</div>}
                {validatingId === t.id && (
                  <div className="pl-6 mt-2 mb-1 space-y-2 bg-secondary/30 p-2 rounded" data-testid={`validate-panel-${t.id}`}>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-muted-foreground">Note %</span>
                      <select value={vPercent} onChange={(e) => setVPercent(e.target.value)} data-testid={`validate-percent-${t.id}`} className="hard-border px-1 py-0.5 text-[11px]">
                        {[0, 25, 50, 75, 100].map((p) => <option key={p} value={p}>{p}%</option>)}
                      </select>
                    </div>
                    <input value={vNote} onChange={(e) => setVNote(e.target.value)} data-testid={`validate-note-${t.id}`}
                      placeholder="Note privée du Kru (ex: garde ta garde haute)…" className="w-full hard-border px-2 py-1 text-[11px]" />
                    <div className="flex gap-2">
                      <button onClick={() => validateTech(t, "valide")} data-testid={`validate-ok-${t.id}`} className="ticker text-[10px] px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-500">✓ Acquis</button>
                      <button onClick={() => validateTech(t, "a_retravailler")} data-testid={`validate-rework-${t.id}`} className="ticker text-[10px] px-2 py-1 rounded bg-amber-500 text-black hover:bg-amber-400">↻ À retravailler</button>
                      <button onClick={() => setValidatingId(null)} className="ticker text-[10px] px-2 py-1 rounded hard-border">Annuler</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function HealthPassPanel({ contract }) {
  const [variant, setVariant] = useState("regular");
  const [validDays, setValidDays] = useState(365);
  const [pass, setPass] = useState(null);
  const [open, setOpen] = useState(false);

  const generate = async () => {
    try {
      const r = await api.post(`/health-pass/${contract.id}/generate?variant=${variant}&valid_days=${validDays}`);
      // For member_view variant, override path to /me/:token
      let publicUrl = r.data.public_url;
      if (variant === "member_view" && publicUrl.includes("/pass/")) {
        publicUrl = publicUrl.replace("/pass/", "/me/");
      }
      setPass({ ...r.data, public_url: publicUrl });
      const label = variant === "competition" ? "passeport athlète" : variant === "member_view" ? "espace membre" : "carte régulière";
      toast.success(`Lien ${label} généré`);
    } catch {
      toast.error("Erreur génération");
    }
  };

  const downloadQR = () => {
    const canvas = document.querySelector('[data-testid="pass-qr-canvas"] canvas');
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `pass-${contract.full_name?.replace(/\s/g, "_")}-${variant}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  const downloadPDF = async () => {
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL;
      const token = JSON.parse(localStorage.getItem("auth_token") || '""');
      const tok = typeof token === "string" ? token : (token?.token || token?.access_token || "");
      const url = `${apiBase}/api/health-pass/${contract.id}/card.pdf?variant=${variant}&valid_days=${validDays}`;
      const r = await fetch(url, { headers: { "Authorization": `Bearer ${tok}` } });
      if (!r.ok) throw new Error("PDF error");
      const blob = await r.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = `carte-${contract.full_name?.replace(/\s/g, "_")}-${variant}.pdf`;
      a.click();
      URL.revokeObjectURL(objUrl);
      toast.success("Carte PDF téléchargée");
    } catch {
      toast.error("Erreur PDF");
    }
  };

  return (
    <div className="hard-border p-3 mb-3 bg-secondary/10" data-testid="health-pass-panel">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between" data-testid="health-pass-toggle">
        <div className="flex items-center gap-2">
          <QrCode size={16} className="text-impact-gold"/>
          <div className="ticker text-xs text-muted-foreground">QR PASSEPORT SANTÉ</div>
        </div>
        <span className="ticker text-xs text-muted-foreground">{open ? "−" : "+"}</span>
      </button>

      {open && (
        <div className="mt-3 pt-3 border-t space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="ticker text-[10px] text-muted-foreground block mb-1">VARIANT</label>
              <select data-testid="pass-variant" value={variant} onChange={(e) => { setVariant(e.target.value); setPass(null); }} className="hard-border px-2 py-1.5 text-sm w-full bg-white">
                <option value="regular">Carte régulière (santé)</option>
                <option value="competition">Passeport compétition</option>
                <option value="member_view">Espace membre (vue élève)</option>
              </select>
            </div>
            <div>
              <label className="ticker text-[10px] text-muted-foreground block mb-1">VALIDE (JOURS)</label>
              <input data-testid="pass-days" type="number" value={validDays} onChange={(e) => { setValidDays(parseInt(e.target.value) || 365); setPass(null); }} className="hard-border px-2 py-1.5 text-sm w-full"/>
            </div>
          </div>
          <button data-testid="pass-generate" onClick={generate} className="w-full bg-impact-gold text-foreground hover:bg-foreground hover:text-white py-2 ticker text-xs transition-colors">
            Générer QR code
          </button>

          {pass && (
            <div className="text-center pt-2" data-testid="pass-result">
              <div data-testid="pass-qr-canvas" className="inline-block hard-border bg-white p-3">
                <QRCodeCanvas value={pass.public_url} size={200} level="H" includeMargin={false}/>
              </div>
              <div className="mt-2 space-y-1">
                <button onClick={downloadQR} data-testid="pass-download"
                  className="ticker text-xs inline-flex items-center gap-1 text-impact-red hover:underline mr-3">
                  <Download size={12}/> PNG QR
                </button>
                <button onClick={downloadPDF} data-testid="pass-download-pdf"
                  className="ticker text-xs inline-flex items-center gap-1 text-impact-gold hover:underline">
                  <Download size={12}/> Carte PDF (recto-verso)
                </button>
                <div className="text-[10px] text-muted-foreground break-all px-2 mt-1">{pass.public_url}</div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
